// Utility functions for handling message URLs and slugs
// URL format: /talk/MESSAGE123ID-this-is-my-message-title

/**
 * Generate a URL-safe slug from a message title
 * @param {string} title - The message title
 * @param {string} messageId - The message ID (used as fallback)
 * @returns {string} URL-safe slug
 */
export const generateSlug = (title, messageId) => {
  if (!title || typeof title !== 'string') {
    return messageId; // Fallback to ID if no title
  }
  
  return title
    .toLowerCase()
    .trim()
    // Replace special characters and spaces with hyphens
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
    // Limit length to 50 characters
    .substring(0, 50)
    // Remove trailing hyphen if truncated
    .replace(/-+$/, '')
    || messageId; // Fallback to ID if slug is empty
};

/**
 * Generate the full URL path for a message
 * @param {Object} message - Message object with id and title
 * @returns {string} URL path
 */
export const getMessageUrl = (message) => {
  if (!message || !message.id) {
    return '/';
  }
  
  const slug = generateSlug(message.title, message.id);
  return `/talk/${message.id}-${slug}`;
};

/**
 * Generate user profile URL from user data.
 * Uses tetherHandle for clean, human-readable URLs.
 * Falls back to userId only when no handle is available.
 * @param {Object} user - User/message object with relevant identifiers
 * @returns {string} URL path to user profile
 */
export const getUserProfileUrl = (user) => {
  if (!user) return '/';

  // Use tetherHandle for clean URLs
  const handle = user.tetherHandle || user.tetherHAndle;
  if (handle) {
    const cleanHandle = handle.replace('@', '').replace(/\s+/g, '').toLowerCase();
    return `/user/${cleanHandle}`;
  }

  // Fall back to userId if no handle available
  if (user.userId) {
    return `/user/${user.userId}`;
  }

  return '/';
};

/**
 * Get display name for a user
 * @param {Object} user - User object
 * @returns {string} Display name
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'Anonymous';
  
  return user.userName || user.tetherHandle || user.tetherHAndle || 'Anonymous';
};

/**
 * Extract message ID from a URL slug
 * @param {string} slugWithId - The slug parameter that may contain ID
 * @returns {string|null} The message ID or null if not found
 */
export const extractMessageId = (slugWithId) => {
  if (!slugWithId) return null;
  
  // If it's just an ID (backwards compatibility)
  if (/^[a-zA-Z0-9]{20,}$/.test(slugWithId)) {
    return slugWithId;
  }
  
  // Extract ID from id-slug format (new format)
  const match = slugWithId.match(/^([a-zA-Z0-9]{20,})-/);
  if (match) {
    return match[1];
  }
  
  // Fallback: extract ID from slug-id format (old format for backwards compatibility)
  const oldMatch = slugWithId.match(/-([a-zA-Z0-9]{20,})$/);
  return oldMatch ? oldMatch[1] : null;
};

/**
 * Check if a slug matches a message title
 * @param {string} slug - The URL slug
 * @param {string} title - The message title
 * @returns {boolean} Whether the slug matches the title
 */
export const slugMatchesTitle = (slug, title) => {
  if (!slug || !title) return false;
  
  const expectedSlug = generateSlug(title, '');
  
  // Extract slug part from id-slug format (new format)
  let actualSlug = slug.replace(/^[a-zA-Z0-9]{20,}-/, '');
  
  // If that didn't work, try old format (slug-id)
  if (actualSlug === slug) {
    actualSlug = slug.replace(/-[a-zA-Z0-9]{20,}$/, '');
  }
  
  return actualSlug === expectedSlug;
};
